// Fetch-Wrapper für Master REST-API mit JWT-Bearer-Header.
// M0: plain fetch ohne TanStack Query — kommt mit M1 wenn echte Daten da sind.
//
// Auth-Pfad: Token wird beim Login in AuthContext gesetzt; alle Aufrufe nutzen
// `getToken()` (Callback aus dem Context).

import { MANAGEMENT_URL } from "@/config/env";

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export class NetworkError extends Error {
  constructor(message = "Cannot reach server") {
    super(message);
    this.name = "NetworkError";
  }
}

interface ApiClientOpts {
  getToken: () => string | null;
  onUnauthorized: () => void;
}

export interface ApiClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  // #270: optionaler Body (Gitea-Commit-Infos beim Datei-Löschen). Rückwärtskompatibel.
  delete<T>(path: string, body?: unknown): Promise<T>;
}

export function createApiClient(opts: ApiClientOpts): ApiClient {
  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
  ): Promise<T> {
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    const token = opts.getToken();
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    let res: Response;
    try {
      res = await fetch(`${MANAGEMENT_URL}${path}`, {
        method,
        headers,
        body: body !== undefined ? JSON.stringify(body) : undefined,
      });
    } catch (err) {
      // fetch wirft bei Netz-Fehlern (CORS, Connection-Refused, DNS)
      throw new NetworkError((err as Error).message);
    }

    // 401 = Token weg/abgelaufen — Logout-Pfad
    if (res.status === 401) {
      opts.onUnauthorized();
      throw new ApiError(401, "Unauthorized");
    }

    if (!res.ok) {
      let parsedBody: unknown;
      const text = await res.text();
      try {
        parsedBody = JSON.parse(text);
      } catch {
        parsedBody = text;
      }
      const message =
        (parsedBody as { error?: string })?.error ?? `HTTP ${res.status}`;
      throw new ApiError(res.status, message, parsedBody);
    }

    if (res.status === 204) {
      return undefined as T;
    }
    return (await res.json()) as T;
  }

  return {
    get: (path) => request("GET", path),
    post: (path, body) => request("POST", path, body),
    patch: (path, body) => request("PATCH", path, body),
    put: (path, body) => request("PUT", path, body),
    delete: (path, body) => request("DELETE", path, body),
  };
}

// Public-Health-Check — kein Auth-Header nötig
export async function checkHealth(): Promise<{ status: string; nats: string }> {
  try {
    const res = await fetch(`${MANAGEMENT_URL}/health`);
    if (!res.ok) throw new ApiError(res.status, "Health check failed");
    return (await res.json()) as { status: string; nats: string };
  } catch (err) {
    if (err instanceof ApiError) throw err;
    throw new NetworkError((err as Error).message);
  }
}
